// Admin-only tool to pin green coordinates for each hole of a course
// using Google Maps coords (right-click → copy coordinates).
//
// Each green can be pinned at three points:
//   • Front (closer to the tee, optional)
//   • Center (legacy `green_lat/lng`, the primary pin)
//   • Back (farther from the tee, optional)
// The Caddie uses front+back to display Front/Center/Back yardages, which is
// the difference between two clubs in the player's hand.
//
// Lives inside AdminPanel as a dedicated tab.
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Flag, ExternalLink, Trash2, Check } from 'lucide-react';

// Accepts:
//   "47.651506, -117.373635"
//   "47.651506,-117.373635"
//   "https://maps.google.com/?q=47.651506,-117.373635"
//   "https://www.google.com/maps/@47.651506,-117.373635,20z"
function parseCoords(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  let m = s.match(/^(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)$/);
  if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  m = s.match(/@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
  if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  m = s.match(/[?&]q=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
  if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  return null;
}

function validCoords(c) {
  return c && c.lat >= -90 && c.lat <= 90 && c.lng >= -180 && c.lng <= 180;
}

function getHoleList(course) {
  const tee = (course?.tees || [])[0];
  if (tee?.holes?.length) return tee.holes;
  return course?.holes || [];
}

const OWNER_EMAIL = 'pzsuave007@gmail.com';

// Edge config — drives endpoint suffix, schema field names, and UI label.
const EDGES = [
  { key: 'front',  label: 'F', latKey: 'green_front_lat', lngKey: 'green_front_lng',
    endpoint: 'green-front',  color: 'text-[#C96A52]' },
  { key: 'center', label: 'C', latKey: 'green_lat',        lngKey: 'green_lng',
    endpoint: 'green-pin',    color: 'text-[#1B3C35]' },
  { key: 'back',   label: 'B', latKey: 'green_back_lat',  lngKey: 'green_back_lng',
    endpoint: 'green-back',   color: 'text-[#4A5D23]' },
];

export default function PinGreensTab() {
  const { user } = useAuth();
  const isOwner = (user?.email || '').toLowerCase() === OWNER_EMAIL;
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState('');
  // inputs[holeNum][edgeKey] = "raw string"
  const [inputs, setInputs] = useState({});
  const [busy, setBusy] = useState({}); // busy[holeNum][edgeKey]

  useEffect(() => {
    axios.get(`${API}/courses`).then(r => setCourses(r.data)).catch(() => {});
  }, []);

  const course = useMemo(
    () => courses.find(c => c.course_id === courseId),
    [courses, courseId]
  );
  const holes = useMemo(() => getHoleList(course), [course]);

  const refreshCourse = async () => {
    const r = await axios.get(`${API}/courses`);
    setCourses(r.data);
  };

  const setBusyEdge = (n, edge, val) => setBusy(b => ({ ...b, [n]: { ...(b[n] || {}), [edge]: val } }));

  const saveEdge = async (hole, edge) => {
    const n = hole.hole || hole.number;
    const raw = inputs[n]?.[edge.key];
    const parsed = parseCoords(raw);
    if (!validCoords(parsed)) {
      toast.error('Invalid coordinates. Paste lat,lng from Google Maps.');
      return;
    }
    setBusyEdge(n, edge.key, true);
    try {
      await axios.put(
        `${API}/courses/${courseId}/holes/${n}/${edge.endpoint}`,
        { lat: parsed.lat, lng: parsed.lng, accuracy: 1 }
      );
      toast.success(`✓ Hole ${n} ${edge.label === 'C' ? 'center' : edge.label === 'F' ? 'front' : 'back'} pinned`);
      setInputs(i => ({ ...i, [n]: { ...(i[n] || {}), [edge.key]: '' } }));
      await refreshCourse();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setBusyEdge(n, edge.key, false);
    }
  };

  const removeEdge = async (hole, edge) => {
    const n = hole.hole || hole.number;
    if (!window.confirm(`Delete ${edge.key} pin for hole ${n}?`)) return;
    setBusyEdge(n, edge.key, true);
    try {
      await axios.delete(`${API}/courses/${courseId}/holes/${n}/${edge.endpoint}`);
      toast.success(`Hole ${n} ${edge.key} pin removed`);
      await refreshCourse();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyEdge(n, edge.key, false);
    }
  };

  return (
    <div className="space-y-4" data-testid="pin-greens-tab-content">
      <Card className="border-[#E2E3DD] shadow-none bg-[#F4E9D8]/40">
        <CardContent className="py-4 text-sm text-[#1B3C35] space-y-1.5">
          <p className="font-bold flex items-center gap-1.5">
            <Flag className="h-4 w-4 text-[#C96A52]" /> Pin greens from Google Maps
          </p>
          <p className="text-xs text-[#6B6E66]">
            Each hole has 3 pins: <strong className="text-[#C96A52]">F</strong> (front, closer to tee) ·
            {' '}<strong className="text-[#1B3C35]">C</strong> (centre, primary) ·
            {' '}<strong className="text-[#4A5D23]">B</strong> (back, farther from tee).
            Pin Centre first; Front and Back are optional but make the Caddie much smarter
            (usually 12-18y between F and B = a full club).
          </p>
        </CardContent>
      </Card>

      <div>
        <Label className="text-[#1B3C35] text-sm font-bold">Course</Label>
        <Select value={courseId} onValueChange={setCourseId}>
          <SelectTrigger className="mt-1 border-[#E2E3DD]" data-testid="pin-greens-course-select">
            <SelectValue placeholder="Choose a course..." />
          </SelectTrigger>
          <SelectContent>
            {courses.map(c => (
              <SelectItem key={c.course_id} value={c.course_id}>
                {c.course_name || c.name} {c.total_par ? `(Par ${c.total_par})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {courseId && holes.length === 0 && (
        <p className="text-sm text-[#6B6E66] py-8 text-center">
          This course has no holes configured yet.
        </p>
      )}

      {holes.length > 0 && (
        <Card className="border-[#E2E3DD] shadow-none">
          <CardContent className="p-0">
            <div className="divide-y divide-[#E2E3DD]">
              {holes.map((h) => {
                const n = h.hole || h.number;
                return (
                  <div key={n}
                    className="py-2.5 px-3 space-y-1.5"
                    data-testid={`pin-green-row-${n}`}>
                    <div className="flex items-center gap-2 text-xs text-[#6B6E66]">
                      <span className="text-sm font-bold text-[#1B3C35] tabular-nums">Hole {n}</span>
                      <span className="text-[#6B6E66] tabular-nums">· Par {h.par ?? '-'}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {EDGES.map(edge => {
                        const lat = h[edge.latKey];
                        const lng = h[edge.lngKey];
                        const pinned = lat != null && lng != null;
                        const raw = inputs[n]?.[edge.key] || '';
                        const isBusy = busy[n]?.[edge.key];
                        return (
                          <div key={edge.key}
                            className="border border-[#E2E3DD] rounded-lg p-2 bg-white"
                            data-testid={`pin-edge-${edge.key}-${n}`}>
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className={`text-xs font-bold ${edge.color}`}>{edge.label}</span>
                              <span className="text-[10px] uppercase tracking-wider text-[#6B6E66]">
                                {edge.key}
                              </span>
                              {pinned && (
                                <Check className="h-3 w-3 text-[#4A5D23] ml-auto shrink-0" />
                              )}
                            </div>
                            {pinned ? (
                              <div className="flex items-center gap-1.5 text-[10px]">
                                <span className="tabular-nums text-[#1B3C35] truncate flex-1 min-w-0">
                                  {lat.toFixed(5)}, {lng.toFixed(5)}
                                </span>
                                <a
                                  href={`https://www.google.com/maps?q=${lat},${lng}`}
                                  target="_blank" rel="noopener noreferrer"
                                  className="text-[#C96A52] hover:underline inline-flex items-center shrink-0"
                                  data-testid={`pin-edge-view-${edge.key}-${n}`}>
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                                {isOwner && (
                                  <button
                                    onClick={() => removeEdge(h, edge)}
                                    className="text-[#C96A52] hover:text-[#1B3C35] shrink-0"
                                    disabled={isBusy}
                                    data-testid={`pin-edge-delete-${edge.key}-${n}`}>
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <Input
                                  value={raw}
                                  onChange={e => setInputs(i => ({
                                    ...i, [n]: { ...(i[n] || {}), [edge.key]: e.target.value }
                                  }))}
                                  placeholder="lat, lng"
                                  className="h-7 border-[#E2E3DD] text-[10px] font-mono placeholder:italic placeholder:text-[#9DA09A]"
                                  data-testid={`pin-edge-input-${edge.key}-${n}`}
                                  disabled={isBusy}
                                />
                                <Button
                                  size="sm"
                                  className="h-6 w-full px-2 bg-[#1B3C35] hover:bg-[#1B3C35]/90 text-[10px]"
                                  disabled={isBusy || !raw.trim()}
                                  onClick={() => saveEdge(h, edge)}
                                  data-testid={`pin-edge-save-${edge.key}-${n}`}>
                                  {isBusy ? '…' : 'Save'}
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
